import React, { useEffect, useRef, useState, useCallback } from 'react';
import {ArrowRight} from 'lucide-react'
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Dither from '@/components/landing/common/Dither';

// ─── Constants ────────────────────────────────────────────────────────────────

const FADE_MS = 220;
const MOBILE_BP = 768;

// ─── Main Component ───────────────────────────────────────────────────────────

const StickyScrollSection = ({slides, visuals, full=false}) => {
  const N = slides.length;
  const navigate = useNavigate();
  const [shownIndex, setShownIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BP
  );

  // JS-driven "sticky" position for the left pane. We never rely on
  // position: sticky here - it silently breaks the moment any ancestor
  // (however far up the tree) sets overflow-x: hidden, because that
  // forces overflow-y into an implicit scroll container and hijacks
  // sticky's containing block. Fixed positioning, computed manually
  // from scroll offsets, sidesteps that entirely.
  const [pinStyle, setPinStyle] = useState({
    position: 'absolute',
    top: 0,
    bottom: 'auto',
    left: 0,
    right: 0,
    width: 'auto',
  });

  const shownRef         = useRef(0);
  const transitioningRef = useRef(false);
  const queuedRef        = useRef(null);
  const timerRef         = useRef(null);
  const containerRef     = useRef(null);
  const leftPaneRef      = useRef(null);

  // Track mobile breakpoint
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BP);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const goTo = useCallback((nextIndex) => {
    if (nextIndex === shownRef.current && queuedRef.current === null) return;
    if (nextIndex === shownRef.current) { queuedRef.current = null; return; }

    queuedRef.current = nextIndex;
    // Already fading with a live timer - let it finish.
    if (transitioningRef.current && timerRef.current !== null) return;

    // (Re)start the fade. StrictMode double-invokes effects in dev, and the
    // cleanup can cancel our timer while a transition is mid-flight; re-entering
    // with a lost timer must re-schedule it or the section stays stuck hidden.
    if (!transitioningRef.current) {
      transitioningRef.current = true;
      setVisible(false);
    }

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const target = queuedRef.current;
      queuedRef.current = null;
      timerRef.current = null;

      if (target !== null && target !== shownRef.current) {
        shownRef.current = target;
        setShownIndex(target);
      }

      setVisible(true);
      transitioningRef.current = false;
    }, FADE_MS);
  }, []);

  // Computes whether the left pane should be pinned to the top of the
  // section, pinned to the bottom (once the section has scrolled past),
  // or fixed to the viewport (while actively inside the section).
  // leftPaneRef never itself gets position: fixed - it stays in normal
  // flow so it keeps reserving layout space and its getBoundingClientRect()
  // stays accurate for computing left/width of the fixed child.
  const updatePin = useCallback(() => {
    const containerEl = containerRef.current;
    const paneEl = leftPaneRef.current;
    if (!containerEl || !paneEl) return;

    const containerRect = containerEl.getBoundingClientRect();
    const paneRect = paneEl.getBoundingClientRect();
    const vh = window.innerHeight;

    if (containerRect.top >= 0) {
      // Section hasn't reached the top of the viewport yet.
      setPinStyle({
        position: 'absolute',
        top: 0,
        bottom: 'auto',
        left: 0,
        right: 0,
        width: 'auto',
      });
    } else if (containerRect.bottom <= vh) {
      // Scrolled past the section - settle at the bottom of the column.
      setPinStyle({
        position: 'absolute',
        top: 'auto',
        bottom: 0,
        left: 0,
        right: 0,
        width: 'auto',
      });
    } else {
      // Actively inside the section - pin to the viewport.
      setPinStyle({
        position: 'fixed',
        top: 0,
        bottom: 'auto',
        left: paneRect.left,
        right: 'auto',
        width: paneRect.width,
      });
    }
  }, []);

  useEffect(() => {
    if (isMobile) return;

    const handleScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect        = el.getBoundingClientRect();
      const totalHeight = el.offsetHeight - window.innerHeight;
      const p           = Math.max(0, Math.min(1, -rect.top / totalHeight));
      const nextIndex   = Math.min(Math.round(p * (N - 1)), N - 1);
      goTo(nextIndex);
      updatePin();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updatePin);
    handleScroll();

    // This section can mount at a non-zero scroll position (browser back /
    // forward with restored scroll) or before sibling lazy chunks above it
    // have rendered. Re-run the math on a few frames and on any page-height
    // change so it never assumes it started at the top of the page.
    const raf1 = requestAnimationFrame(handleScroll);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(handleScroll));
    const raf3 = requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(handleScroll)));

    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && document.body) {
      ro = new ResizeObserver(handleScroll);
      ro.observe(document.body);
    }

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      cancelAnimationFrame(raf3);
      if (ro) ro.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updatePin);
      clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [goTo, isMobile, updatePin]);

  // ── Mobile layout: each feature is a self-contained stacked section ──
  if (isMobile) {
    return (
      <MobileOuter>
        {slides.map((f) => {
          const VisualComponent = visuals[f.visual];
          return (
            <MobileSection key={f.visual}>
              <MobileVisualWrap>
                <VisualComponent />
              </MobileVisualWrap>
              <MobileCopy>
                <Badge>{f.badge}</Badge>
                <Heading className="sans">
                  {f.heading.split('\n').map((line, j) => (
                    <React.Fragment key={j}>{line}{j === 0 && <br />}</React.Fragment>
                  ))}
                </Heading>
                <Body>{f.body}</Body>
                <CtaButton onClick={() => navigate(f.ctaUrl)}>
                  <CtaArrow>↳</CtaArrow> {f.cta}
                </CtaButton>
              </MobileCopy>
            </MobileSection>
          );
        })}
      </MobileOuter>
    );
  }

  // ── Desktop layout: JS-pinned column instead of CSS sticky ──
  const feature = slides[shownIndex];

  return (
    <Outer ref={containerRef} $full={full}>
      <LeftPane ref={leftPaneRef} $full={full}>
        <PinnedContent style={pinStyle}>
          <ProgressDots>
            {slides.map((_, i) => (
              <ProgressDot key={i} $active={i === shownIndex} />
            ))}
          </ProgressDots>

          <Dither zIndex={-2} shape="square" backgroundColor="#fff" inkColor="#eee" />

          <CopyInner>
            <CopyBlock $visible={visible}>
              <Badge>{feature.badge}</Badge>
              <Heading className="sans">
                {feature.heading.split('\n').map((line, j) => (
                  <React.Fragment key={j}>{line}{j === 0 && <br />}</React.Fragment>
                ))}
              </Heading>
              <Body>{feature.body}</Body>
              <CtaButton onClick={() => { navigate(feature.ctaUrl); }}>
                {feature.cta}
                <CtaArrow> <ArrowRight width={14}/> </CtaArrow> 
              </CtaButton>
            </CopyBlock>
          </CopyInner>
        </PinnedContent>
      </LeftPane>

      <RightPane $full={full}>
        {slides.map((f) => {
          const VisualComponent = visuals[f.visual];
          return (
            <VisualPanel key={f.visual} $full={full}>
              <VisualComponent />
            </VisualPanel>
          );
        })}
      </RightPane>
    </Outer>
  );
};

export default StickyScrollSection;

// ─── Mobile Layout Styled Components ─────────────────────────────────────────

const MobileOuter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 72px;
  padding: 48px 20px 64px;
  padding-top: 80px;
  overflow-x: hidden;
`;

const MobileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding-bottom: 100px;
  border-bottom: 1px solid rgba(var(--greyr), 0.3);
  &:last-of-type{
    border-bottom: none;
    padding-bottom: 20px;
  }
`;

const MobileVisualWrap = styled.div`
  width: 100%;
  padding-bottom: 40px;
`;

const MobileCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// ─── Layout & Copy Styled Components ─────────────────────────────────────────

const Outer = styled.div`
  position: relative;
  display: flex;
  ${props => (props.$full) ? '' : 'max-width: 1920px;' }
  margin: 0 auto;
  overflow: hidden;
`;

// No longer position: sticky - stays in normal flow so it keeps
// reserving correct layout space while PinnedContent (below) handles
// the actual fixed/absolute positioning via JS.
const LeftPane = styled.div`
  position: relative;
  width: 50%;
  align-self: stretch;

  canvas{
    opacity: 0.6;

  }

  ${props => (props.$full) ?  '' : `flex-shrink: 0;`}

  @media (max-width: 1103px) {
    width: 40%;
    margin-right: 10%;
  }
`;

// Receives inline style (position/top/bottom/left/right/width) computed
// in updatePin(). This is the element that visually "sticks."
const PinnedContent = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  padding: 0 40px;
  box-sizing: border-box;
`;

const CopyInner = styled.div`
  position: relative;
  width: 100%;
  pointer-events: none;
`;

const CopyBlock = styled.div`
  width: 100%;
  display: flex;
  pointer-events: none;
  flex-direction: column;
  gap: 24px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0px' : '-6px')});
  transition: opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease;
`;

const RightPane = styled.div`
  width: ${props => (props.$full) ? '50%': 'calc(45% - 30px)'};

  ${props => (props.$full) ?  '' : `
    flex-shrink: 0;
  `}

  z-index: 1;
  background-color: white;

`;

const VisualPanel = styled.div`

  min-height: ${props => (props.$full) ? '600px': '800px'};
  @media (max-width: 768px) {
    min-height: 0px;
  }

  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  color: #555;
  border: 1px solid #ccc;
  border-radius: 999px;
  padding: 4px 12px;
  width: fit-content;

  &::before { content: '•'; color: #222; }
`;

const Heading = styled.h2`
  font-size: clamp(36px, 4vw, 58px);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
  text-transform: uppercase;
  color: #111;
  margin: 0;
`;

const Body = styled.p`
  font-size: 16px;
  line-height: 1.7;
  background: white;
  color: #666;
  max-width: 440px;
  width: 80%;
  margin: 0;

  @media (max-width: 968px) {
    max-width: 100%;
    font-size: 15px;
  }
`;

const CtaButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  background: #111;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  padding: 12px 26px;
  cursor: pointer;
  width: fit-content;
  transition: background 0.2s, transform 0.2s;
  pointer-events: all;

  &:hover { background: #333; transform: translateY(-1px); }

  @media (max-width: 968px) {
    padding: 13px 22px;
    font-size: 14px;
  }
`;

const CtaArrow = styled.span`
  padding: 0;
  margin: 0;
  line-height: 1;
`;

const ProgressDots = styled.div`
  position: absolute;
  bottom: 80px;
  left: 40px;
  display: flex;
  gap: 8px;
`;

const ProgressDot = styled.div`
  width: ${({ $active }) => ($active ? '24px' : '8px')};
  height: 8px;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? '#111' : '#ccc')};
  transition: all 0.35s cubic-bezier(0.22, 1, 0.36, 1);
`;
